import download from 'downloadjs';
import { isNull } from 'lodash/fp';
import { toast } from 'react-toastify';
import { push } from 'redux-first-history';
import { all, call, put, select, takeLatest, takeLeading } from 'redux-saga/effects';

import { askPortfolioQuestion, deletePortfolioAnalysisTemplate, downloadPortfolioAnalytics, emailPortfolioAnalytics, getPortfolioAnalysisTemplates, getPortfolioFields, savePortfolioAnalysisTemplate } from '../../../../services/analytics';
import { getChartBase64String } from '../../../../utils/analytics';
import { dateNow, formatDate } from '../../../../utils/luxon';
import { dotsToDashes } from '../../../../utils/regex-utils';
import { MimeType } from '../../../constants/MimeType';
import { DatasetType } from '../../../datasets/store/types';
import { openDocumentAndInstance } from '../../../documents/my-documents/store/actions';
import { GraphKeyAndColour } from '../../../shared/analytics/PieChart';
import { getAllClientDatasets } from '../../client-datasets/store/selectors';
import { ClientDatasets } from '../../client-datasets/store/types';
import { getAllDocumentNames } from '../../documents/store/selectors';
import { DocumentNameDB } from '../../documents/store/types';
import { askPortfolioQuestionFailed, askPortfolioQuestionStarted, askPortfolioQuestionSuccessful, deletePortfolioAnalyticsTemplateFailed, deletePortfolioAnalyticsTemplateStarted, deletePortfolioAnalyticsTemplateSuccessful, downloadPortfolioAnalyticsFailed, downloadPortfolioAnalyticsSuccessful, emailPortfolioAnalyticsFailed, emailPortfolioAnalyticsSuccessful, fetchDocumentFieldsFailed, fetchDocumentFieldsStarted, fetchDocumentFieldsSuccessful, getPortfolioAnalyticsTemplatesFailed, getPortfolioAnalyticsTemplatesSuccessful, redirectToDocument, savePortfolioAnalyticsTemplateFailed, savePortfolioAnalyticsTemplateStarted, savePortfolioAnalyticsTemplateSuccessful, setFilename, setPieChartBase64, setPortfolioAnalyticsTemplate, toggleDownloadModal, updatePortfolioQuestion } from './actions';
import { getDownloadFilename, getGraphKeyColours, getPieChartBase64, getPortfolioAnalyticsTemplateUsed, getPortfolioQuestion, getPortfolioResponse, getReportEmail } from './selectors';
import { PortfolioAnalysisTemplate, PortfolioAnalyticsActionTypes, PortfolioAnalyticsDownloadEmailParams, PortfolioAnalyticsReportEmail, PortfolioDocumentField, PortfolioQuestion, PortfolioQuestionResponse } from './types';

export function* attemptFetchAvailablePortfolioFields({ payload }: ReturnType<typeof fetchDocumentFieldsStarted>) {
    try {
        const availableFields: PortfolioDocumentField[] = yield call(getPortfolioFields, { datasetId: parseInt(payload) });
        yield put(fetchDocumentFieldsSuccessful(availableFields));
    } catch (e) {
        yield put(fetchDocumentFieldsFailed((e as Error).message));
        toast.error('Unable to retrieve the available report fields. Please try again.');
    }
}

function* fetchAvailableReportFieldsWatcher() {
    yield takeLeading(PortfolioAnalyticsActionTypes.FETCH_DOCUMENT_FIELDS_STARTED, attemptFetchAvailablePortfolioFields);
}

export function* checkPortfolioQuestionUpdated({ payload }: ReturnType<typeof updatePortfolioQuestion>) {
    const { key, value } = payload;
    if (key === 'parentDatasetId') {
        if (!isNull(value)) {
            const datasetId = value as number;
            yield put(fetchDocumentFieldsStarted(datasetId.toString()));
        } else {
            yield put(fetchDocumentFieldsSuccessful([]));
        }
    }
}

function* portfolioSurveyUpdatedWatcher() {
    yield takeLatest(PortfolioAnalyticsActionTypes.UPDATE_PORTFOLIO_QUESTION, checkPortfolioQuestionUpdated);
}

export function* attemptAskPortfolioQuestion({ payload }: ReturnType<typeof askPortfolioQuestionStarted>) {
    try {
        const response: PortfolioQuestionResponse = yield call(askPortfolioQuestion, payload);
        yield put(askPortfolioQuestionSuccessful(response));
    } catch (e) {
        const errorMessage = (e as Error).message;
        yield put(askPortfolioQuestionFailed(errorMessage));
        const toastMessage = errorMessage === 'Internal server error' ? 'Unable to ask the question. Please try again.' : errorMessage;
        toast.error(toastMessage);
    }
}

function* askPortfolioQuestionWatcher() {
    yield takeLatest(PortfolioAnalyticsActionTypes.ASK_PORTFOLIO_QUESTION_STARTED, attemptAskPortfolioQuestion);
}

export function* attemptRedirectToDocumentInstance({ payload }: ReturnType<typeof redirectToDocument>) {
    const { documentId, datasetInstanceId, fieldToOpen } = payload;
    yield put(push({ pathname: `/documents/analysis/${documentId}/${datasetInstanceId}`, state: { from: '/admin/intelligent-portfolio-analysis' } }));
    yield put(openDocumentAndInstance(documentId.toString(), datasetInstanceId.toString(), false, fieldToOpen, true));
}

function* goToDocumentInstance() {
    yield takeLeading(PortfolioAnalyticsActionTypes.REDIRECT_TO_DOCUMENT, attemptRedirectToDocumentInstance);
}

export function* constructDownloadEmailParams() {
    const { results, graphData, summary }: PortfolioQuestionResponse = yield select(getPortfolioResponse);
    const graphKeyColours: GraphKeyAndColour = yield select(getGraphKeyColours);
    const { parentDatasetId, fieldLabel, question, fieldType, datasetType, filters: { documentNameIds } }: PortfolioQuestion = yield select(getPortfolioQuestion);
    const datasets: ClientDatasets[] = yield select(getAllClientDatasets);
    const allDocumentNames: DocumentNameDB[] = yield select(getAllDocumentNames);
    const pieChartBase64: string = yield select(getPieChartBase64);

    const agreementType = datasets.find(({ datasetId }) => datasetId === parentDatasetId)?.agreementType || '';
    const documentNames = allDocumentNames.filter(({ datasetId }) => datasetId === parentDatasetId).filter(({ documentNameId }) => documentNameIds.includes(documentNameId!)).map(({ documentName }) => documentName).join(', ');
    const graphDataWithColour = graphData.map(data => ({ ...data, color: graphKeyColours[data.key] }));
    const doubleRowHeight = navigator.userAgent.indexOf('Win') !== -1;
    const isForm = datasetType === DatasetType.FORM;
    return { pieChartBase64, agreementType, documentNames, graphData: graphDataWithColour, doubleRowHeight, isForm, summary, data: results, fieldLabel, question, fieldType: fieldType! };
}

export function* attemptDownloadPortfolioAnalytics() {
    try {
        const filename: string = yield select(getDownloadFilename);
        const baseDownloadParams: PortfolioAnalyticsDownloadEmailParams = yield call(constructDownloadEmailParams);
        const formattedFilename = dotsToDashes(filename);
        const analytics: Blob = yield call(downloadPortfolioAnalytics, { filename: formattedFilename, ...baseDownloadParams });
        yield call(download, analytics, formattedFilename, MimeType.EXCEL);
        yield put(downloadPortfolioAnalyticsSuccessful());
    } catch (e) {
        const errorMessage = (e as Error).message;
        yield put(downloadPortfolioAnalyticsFailed(errorMessage));
        toast.error(errorMessage);
    }
}

function* downloadPortfolioAnalyticsWatcher() {
    yield takeLeading(PortfolioAnalyticsActionTypes.DOWNLOAD_PORTFOLIO_ANALYTICS_STARTED, attemptDownloadPortfolioAnalytics);
}

export function* attemptEmailPortfolioAnalytics() {
    try {
        const reportEmail: PortfolioAnalyticsReportEmail | null = yield select(getReportEmail);
        if (!isNull(reportEmail)) {
            const { filename, emailBody, height, svgId, width, userIds } = reportEmail;
            const baseEmailParams: PortfolioAnalyticsDownloadEmailParams = yield call(constructDownloadEmailParams);
            const pieChartBase64: string = yield call(getChartBase64String, svgId, 600, 600, width, height, 600, 600);
            yield call(emailPortfolioAnalytics, { ...baseEmailParams, filename, emailBody, userIds, pieChartBase64 });
        }
        yield put(emailPortfolioAnalyticsSuccessful());
        toast('Successfully emailed results.');
    } catch (e) {
        const errorMessage = (e as Error).message;
        yield put(emailPortfolioAnalyticsFailed(errorMessage));
        toast.error(errorMessage);
    }
}

function* emailPortfolioAnalyticsWatcher() {
    yield takeLeading(PortfolioAnalyticsActionTypes.EMAIL_PORTFOLIO_ANALYTICS_STARTED, attemptEmailPortfolioAnalytics);
}

export function* setDownloadMetaOnModalOpen({ payload }: ReturnType<typeof toggleDownloadModal>) {
    if (isNull(payload)) {
        yield put(setPieChartBase64(''));
        yield put(setFilename(null));
    } else {
        const { svgId, width, height } = payload;
        const { parentDatasetId, fieldLabel }: PortfolioQuestion = yield select(getPortfolioQuestion);
        const datasets: ClientDatasets[] = yield select(getAllClientDatasets);
        const datasetName = datasets.find(({ datasetId }) => datasetId === parentDatasetId)?.agreementType || '';
        const date = formatDate(dateNow());
        const filename = `${datasetName}_${fieldLabel}_${date}`;
        const pieChartBase64: string = yield call(getChartBase64String, svgId, 600, 600, width, height, 600, 600);
        yield put(setPieChartBase64(pieChartBase64));
        yield put(setFilename(filename));
    }
}

function* setDownloadMetaOnModalOpenWatcher() {
    yield takeLeading(PortfolioAnalyticsActionTypes.TOGGLE_DOWNLOAD_MODAL, setDownloadMetaOnModalOpen);
}

export function* attemptFetchAllPortfolioAnalyticsTemplates() {
    try {
        const templates: PortfolioAnalysisTemplate[] = yield call(getPortfolioAnalysisTemplates);
        yield put(getPortfolioAnalyticsTemplatesSuccessful(templates));
    } catch (e) {
        const errorMessage = (e as Error).message;
        yield put(getPortfolioAnalyticsTemplatesFailed(errorMessage));
        toast.error(errorMessage);
    }
}

function* fetchAllPortfolioAnalyticsTemplatesWatcher() {
    yield takeLeading(PortfolioAnalyticsActionTypes.GET_ALL_PORTFOLIO_ANALYTICS_TEMPLATES_STARTED, attemptFetchAllPortfolioAnalyticsTemplates);
}

export function* attemptDeletePortfolioAnalyticsTemplate({ payload }: ReturnType<typeof deletePortfolioAnalyticsTemplateStarted>) {
    try {
        const templates: PortfolioAnalysisTemplate[] = yield call(deletePortfolioAnalysisTemplate, payload);
        yield put(deletePortfolioAnalyticsTemplateSuccessful(templates));
        toast('Successfully deleted template.');
    } catch (e) {
        const errorMessage = (e as Error).message;
        yield put(deletePortfolioAnalyticsTemplateFailed(errorMessage));
        toast.error('Unable to delete the template. Please try again.');
    }
}

function* deletePortfolioAnalyticsTemplate() {
    yield takeLatest(PortfolioAnalyticsActionTypes.DELETE_PORTFOLIO_ANALYTICS_TEMPLATE_STARTED, attemptDeletePortfolioAnalyticsTemplate);
}

export function* attemptSavePortfolioAnalyticsTemplate({ payload }: ReturnType<typeof savePortfolioAnalyticsTemplateStarted>) {
    try {
        const templateUsed: PortfolioAnalysisTemplate = yield select(getPortfolioAnalyticsTemplateUsed);
        const updatedTemplate = { ...templateUsed, templateName: payload.templateName, metaData: payload.metaData };
        const templates: PortfolioAnalysisTemplate[] = yield call(savePortfolioAnalysisTemplate, payload);
        yield put(savePortfolioAnalyticsTemplateSuccessful(templates));
        yield put(setPortfolioAnalyticsTemplate(updatedTemplate));
        toast('Successfully saved template.');
    } catch (e) {
        const errorMessage = (e as Error).message;
        yield put(savePortfolioAnalyticsTemplateFailed(errorMessage));
        toast.error('Unable to save the template. Please try again.');
    }
}

function* savePortfolioAnalyticsTemplate() {
    yield takeLeading(PortfolioAnalyticsActionTypes.SAVE_PORTFOLIO_ANALYTICS_TEMPLATE_STARTED, attemptSavePortfolioAnalyticsTemplate);
}

export function* portfolioAnalyticsSaga() {
    yield all([
        fetchAvailableReportFieldsWatcher(),
        portfolioSurveyUpdatedWatcher(),
        askPortfolioQuestionWatcher(),
        goToDocumentInstance(),
        downloadPortfolioAnalyticsWatcher(),
        setDownloadMetaOnModalOpenWatcher(),
        fetchAllPortfolioAnalyticsTemplatesWatcher(),
        deletePortfolioAnalyticsTemplate(),
        savePortfolioAnalyticsTemplate(),
        emailPortfolioAnalyticsWatcher()
    ]);
}
